/**
 * EditUserDrawer — drawer scivolante per modifica dettagli utente.
 *
 * Fetch async di AdminUserDetail al mount, form prefilled, save via
 * adminApi.users.update con reason obbligatorio.
 */
import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminApi, type AdminUserDetail } from '@/services/adminApi';

export interface EditUserDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uid: string | null;
  onSaved?: () => void;
}

export function EditUserDrawer({ open, onOpenChange, uid, onSaved }: EditUserDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [form, setForm] = useState({
    displayName: '',
    accountType: '',
    locale: '',
    timezone: '',
    reason: '',
  });

  useEffect(() => {
    if (!open || !uid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await adminApi.users.detail(uid);
        if (cancelled) return;
        setDetail(d);
        setForm({
          displayName: d.displayName ?? '',
          accountType: d.accountType ?? 'individual',
          locale: d.locale ?? '',
          timezone: d.timezone ?? '',
          reason: '',
        });
      } catch (err) {
        if (!cancelled) toast.error((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, uid]);

  const handleSave = async () => {
    if (!uid || !detail || !form.reason.trim() || busy) return;
    setBusy(true);
    try {
      const patch: Parameters<typeof adminApi.users.update>[1] = {
        reason: form.reason.trim(),
      };
      if (form.displayName !== detail.displayName) patch.displayName = form.displayName;
      if (form.accountType !== detail.accountType) patch.accountType = form.accountType;
      if (form.locale !== (detail.locale ?? '')) patch.locale = form.locale;
      if (form.timezone !== (detail.timezone ?? '')) patch.timezone = form.timezone;

      await adminApi.users.update(uid, patch);
      toast.success('User updated');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Modifica utente</SheetTitle>
          <SheetDescription>
            {detail ? (
              <>
                Email: <code className="text-xs">{detail.email}</code>
                {detail.uid && <> · uid: <code className="text-xs">{detail.uid.slice(0, 12)}...</code></>}
              </>
            ) : 'Caricamento dettagli...'}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="py-12 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && detail && (
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="displayName">Nome visualizzato</Label>
              <Input
                id="displayName"
                value={form.displayName}
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="accountType">Account type</Label>
              <Select
                value={form.accountType}
                onValueChange={(v) => setForm((f) => ({ ...f, accountType: v }))}
                disabled={busy}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual (SME)</SelectItem>
                  <SelectItem value="agency_owner">Agency owner</SelectItem>
                  <SelectItem value="invited_member">Invited member</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                ⚠️ Cambiare accountType senza coordinare con migrazioni billing può creare drift Firestore ↔ Autumn. Usa con cautela.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="locale">Locale</Label>
              <Input
                id="locale"
                placeholder="it / en"
                value={form.locale}
                onChange={(e) => setForm((f) => ({ ...f, locale: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="timezone">Timezone</Label>
              <Input
                id="timezone"
                placeholder="Europe/Rome"
                value={form.timezone}
                onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5 pt-2 border-t border-border/50">
              <Label htmlFor="reason">Reason (richiesto per audit log)</Label>
              <Input
                id="reason"
                placeholder="Brief reason for change..."
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                disabled={busy}
              />
            </div>
          </div>
        )}

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={busy || !form.reason.trim() || !detail}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Salva
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
